import InfoOutlined from "@mui/icons-material/InfoOutlined";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Checkbox from "../../../../../../Components/CheckBox/Checkbox";
import { Handle, Position } from "reactflow";
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import "./LLMNode.css";
import axios from "axios";
import {
  CloseOutlined,
  Fullscreen,
  FullscreenExit,
  HelpOutline,
} from "@mui/icons-material";
import { updateLocalStorage } from "../../../../AppDrawerNav";
import { getDataset } from "../../../../../../utils/IndexedDBDexieUtility";
import {
  topNavNode,
  topNavNodeHidden,
  topNavNodeText,
} from "../GeneralNodeStyles";
import { generationDefault } from "../../../../../../utils/globalBotData";
import theme, {
  createLLMTypography,
  scrollbarStyles,
  tabsStyle,
} from "../../../../../../Constants/theme";
import { MODEL_GROUPS, useLocalStorageState } from "./LLMUtils";
import LLMNodeSelectedValues from "./LLMNodeSelectedValues";
import FoundationalModels from "./FoundationalModels";
import FinetuningNode from "./FinetuningNode";
import { deleteIconStyles } from "../../../../../../Constants/buttonStyles";

const LLMNode = memo(({ data }) => {
  const { botData, setBotData, nodeId, handleGenerationTutorial } = data;
  const organization = useSelector((state) => state.organization);
  const { user } = useSelector((state) => state.auth);

  const [temperature, setTemperature] = useState(() => {
    if (nodeId && botData?.workflow?.[nodeId]?.llm_params?.temperature) {
      return Object.keys(
        botData.workflow[nodeId].llm_params.temperature
      ).join();
    }
    return "0";
  });
  const [outputTokenLimit, setoutputTokenLimit] = useState(0);
  const [epoch, setEpoch] = useState(1);

  const [batchSize, setbatchSize] = useState(2);

  const [learningRate, setLearningRate] = useState(0.0001);

  const [learningRateM, setLearningRateM] = useState(1); // LR multiplier

  const [rank, setRank] = useState(1);

  const [aplhaRankRatio, setAlphaRankRatio] = useState(1);

  const [llm_select, set_llm_select] = useState("gpt-4o-mini-2024-07-18"); // for ft llm
  const [llm_select_open, setllm_select_open] = useState(false); // state to control open/close select

  const [opm_select, set_opm_select] = useState("grid-search");
  const [opm_select_open, setopm_select_open] = useState(false);

  const [dataset, setDataset] = useState(
    botData?.finetuning_dataset[0]?.name || ""
  ); // TODO stores the current  - change later

  const [LLM, setLLM] = useState(
    botData?.workflow?.[nodeId]?.llm_params?.llm ||
      generationDefault.llm_params.llm
  );

  const [LLMFinetuning, setLLMFinetuning] = useState(
    botData["llm-finetuning"] ? botData["llm-finetuning"] : false
  ); // enable/disable ft
  const [LLMFoundational, setLLMFoundational] = useState(
    localStorage.getItem(`LLMFoundational_${nodeId}`) === "false" ? false : true
  );

  const [expandedPanel, setExpandedPanel] = useLocalStorageState(
    `expanded_${nodeId}`,
    MODEL_GROUPS[0].key
  );
  const [advancedOption, setAdvancedOption] = useState(
    localStorage.getItem(`adnvancedOption_${nodeId}`) === "true" ? true : false
  );
  const [hidden, setHidden] = useState(() => {
    const stored = localStorage.getItem(`hidden_${nodeId}`);
    return stored !== null ? JSON.parse(stored) : false; // ← default now false
  });

  const [basicPrompt, setBasicPrompt] = useState(
    botData?.prompt_combinations || {
      "zero-shot": "You are a helpful assistant.",
    }
  );

  const selectRefLLM = useRef(null); // used in llm ft select
  const selectRefOPM = useRef(null); //used in optm select
  const llm_select_options = ["llama-3-8b", "gpt-4o-mini-2024-07-18"];
  const opm_select_options = ["grid-search", "bayesian-optimization"];

  const [selectedTab, setSelectedTab] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // used for ft llm select
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRefLLM.current && llm_select_open) {
        setllm_select_open(false);
        selectRefLLM.current = null;
      }
      if (selectRefOPM.current && opm_select_open) {
        setopm_select_open(false);
        selectRefOPM.current = null;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [llm_select_open, opm_select_open]);

  useEffect(() => {
    if (llm_select_open && selectRefLLM.current) {
      selectRefLLM.current.focus();
    }

    if (opm_select_open && selectRefOPM.current) {
      selectRefOPM.current.focus();
    }
  }, [llm_select_open, opm_select_open]);

  // updates ft local states based on botdata
  useEffect(() => {
    // batchsize
    const initialBatchSize =
      botData?.workflow?.[nodeId]?.finetuning_values?.batchSize;
    if (initialBatchSize) {
      // Convert the object keys to a comma-separated string
      const initString = Object.keys(initialBatchSize).join(",");
      setbatchSize(initString);
    }

    // epoch
    const initialEpoch = botData?.workflow?.[nodeId]?.finetuning_values?.epoch;
    if (initialEpoch) {
      // Convert the object keys to a comma-separated string
      const initString = Object.keys(initialEpoch).join(",");
      setEpoch(initString);
    }

    // learning rate
    const initialLR =
      botData?.workflow?.[nodeId]?.finetuning_values?.learningRate;
    if (initialLR) {
      // Convert the object keys to a comma-separated string
      const initString = Object.keys(initialLR).join(",");
      setLearningRate(initString);
    }

    // Rank
    const initialRank = botData?.workflow?.[nodeId]?.finetuning_values?.rank;
    if (initialRank) {
      // Convert the object keys to a comma-separated string
      const initString = Object.keys(initialRank).join(",");
      setRank(initString);
    }

    // Rank
    const initialAplhpaRank =
      botData?.workflow?.[nodeId]?.finetuning_values?.alphaRank;
    if (initialAplhpaRank) {
      // Convert the object keys to a comma-separated string
      const initString = Object.keys(initialAplhpaRank).join(",");
      setAlphaRankRatio(initString);
    }

    // ft llm
    const llmData = botData?.workflow?.[`${nodeId}`]?.finetuning_values?.llm;
    if (llmData) {
      const selectedLlm = Object.keys(llmData).find(
        (key) => llmData[key] === true
      );
      set_llm_select(selectedLlm || "gpt-4o-mini-2024-07-18");
    }
  }, [botData, nodeId]);

  // updates ft datasets from indexdb and localstorage to local state
  // and updates llm prompts and states from local storage
  useEffect(() => {
    // Helper function to parse JSON safely

    const loadData = async () => {
      try {
        // Load other states from localStorage as before
        const updateStateFromLocalStorage = (key, setter) => {
          const storedValue = localStorage.getItem(key);
          if (storedValue) {
            const parsedValue = JSON.parse(storedValue);
            if (parsedValue) {
              setter(parsedValue);
            }
          }
        };
        // Update states from localStorage
        const storedDataset = await getDataset(`finetuning_dataset_${nodeId}`);

        await updateStateFromLocalStorage(
          `temperature_${nodeId}`,
          setTemperature
        );
        await updateStateFromLocalStorage(
          `learningRateM_${nodeId}`,
          setLearningRateM
        );
        await updateStateFromLocalStorage(
          `learningRate_${nodeId}`,
          setLearningRate
        );
        await updateStateFromLocalStorage(`rank_${nodeId}`, setRank);
        await updateStateFromLocalStorage(
          `aplhaRankRatio_${nodeId}`,
          setAlphaRankRatio
        );
        await updateStateFromLocalStorage(
          `llm_select_${nodeId}`,
          set_llm_select
        );
        await updateStateFromLocalStorage(`dataset_${nodeId}`, setDataset);
        await updateStateFromLocalStorage(`LLM_${nodeId}`, setLLM);
        await updateStateFromLocalStorage(
          `LLMFinetuning_${nodeId}`,
          setLLMFinetuning
        );
        await updateStateFromLocalStorage(
          `LLMFoundational_${nodeId}`,
          setLLMFoundational
        );
        await updateStateFromLocalStorage(
          `advancedOption_${nodeId}`,
          setAdvancedOption
        );
        await updateStateFromLocalStorage(`hidden_${nodeId}`, setHidden);
        await updateStateFromLocalStorage(
          `prompt_combinations_${nodeId}`,
          setBasicPrompt
        );
        await updateStateFromLocalStorage(`batchSize_${nodeId}`, setbatchSize);
        await updateStateFromLocalStorage(`epoch_${nodeId}`, setEpoch);
        await updateStateFromLocalStorage(
          `opm_select_${nodeId}`,
          set_opm_select
        );

        // Update botData with the new values
        setBotData((prevBotData) => ({
          ...prevBotData,
          "llm-finetuning": LLMFinetuning,
          finetuning_dataset: storedDataset || {},
          optimization_method: opm_select,
          workflow: {
            ...prevBotData.workflow,
            [`${nodeId}`]: {
              ...(prevBotData.workflow?.[`${nodeId}`] || {}),
              llm_params: {
                ...(prevBotData?.workflow?.[`${nodeId}`]?.llm_params || {}),
                temperature: temperature
                  .toString()
                  .split(",")
                  .reduce((acc, num) => {
                    acc[num] = true;
                    return acc;
                  }, {}),
                outputTokenLimit: outputTokenLimit
                  .toString()
                  .split(",")
                  .reduce((acc, num) => {
                    acc[num] = true;
                    return acc;
                  }, {}),
              },
              finetuning_values: {
                ...(prevBotData?.workflow?.[`${nodeId}`]?.finetuning_values ||
                  {}),
                llm: llm_select
                  .toString()
                  .split(",")
                  .reduce((acc, num) => {
                    acc[num] = true;
                    return acc;
                  }, {}),
                epoch: epoch
                  .toString()
                  .split(",")
                  .reduce((acc, num) => {
                    acc[num] = true;
                    return acc;
                  }, {}),
                learningRate: learningRate
                  .toString()
                  .split(",")
                  .reduce((acc, num) => {
                    acc[num] = true;
                    return acc;
                  }, {}),
                batchSize: batchSize
                  .toString()
                  .split(",")
                  .reduce((acc, num) => {
                    acc[num] = true;
                    return acc;
                  }, {}),
              },
            },
          },
        }));
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadData();
  }, [nodeId, setBotData, advancedOption, hidden, LLMFoundational]);

  const handleLLMSelectChange = (event) => {
    if (event.target.value) {
      set_llm_select(event.target.value);
      const llmValues = {
        "gpt-4o-mini-2024-07-18": false,
        "llama-3-8b": false,
      };
      localStorage.setItem(
        `llm_select_${nodeId}`,
        JSON.stringify(event.target.value)
      );
      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],
            finetuning_values: {
              ...generationDefault.finetuning_values,
              llm: { ...llmValues, [event.target.value]: true },
            },
          },
        },
      }));

      if (event.target.value === "gpt-4o-mini-2024-07-18") {
        setLearningRateM(0.1);
        setLearningRate(1);
        localStorage.setItem(`learningRateM_${nodeId}`, JSON.stringify(0.1));
        localStorage.setItem(`learningRate_${nodeId}`, JSON.stringify(1));
      } else {
        setLearningRateM(0.1);
        setLearningRate(0.0001);
        localStorage.setItem(`learningRateM_${nodeId}`, JSON.stringify(0.1));
        localStorage.setItem(`learningRate_${nodeId}`, JSON.stringify(0.0001));
      }
    }
    setllm_select_open(false);
  };

  const handleOpmSelectChange = (event) => {
    if (event.target.value) {
      set_opm_select(event.target.value);
      const llmValues = {
        "gpt-4o-mini-2024-07-18": false,
        "llama-3-8b": false,
      };
      localStorage.setItem(
        `opm_select_${nodeId}`,
        JSON.stringify(event.target.value)
      );
      data.setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],
            finetuning_values: {
              ...generationDefault.finetuning_values,
              llm: { ...llmValues, [llm_select]: true },
              optimization_method: event.target.value,
            },
          },
        },
      }));
    }
    setopm_select_open(false);
  };

  const handleOpmSelectClick = () => {
    if (LLMFinetuning && !opm_select_open) {
      setopm_select_open(true);
      selectRefOPM?.current?.focus();
    }
  };

  const handleLLMSelectClick = () => {
    if (LLMFinetuning && !llm_select_open) {
      setllm_select_open(true);
      selectRefLLM?.current?.focus();
    }
  };

  const handleOpenSideBar = useCallback((infotype, event) => {
    event.stopPropagation(); // Prevent event from bubbling up to ReactFlow
    scroll();
    updateLocalStorage("open_sidebar", "true");
    updateLocalStorage("type", infotype);
  }, []);

  const scroll = () => {
    setTimeout(() => {
      const closeButton = document.querySelector("#btn-close-sidebar");
      if (closeButton) {
        closeButton.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleLLMChange = (event, type) => {
    const llm = {
      ...LLM,
    };
    llm[type] = event.target.checked;

    if (!Object.values(llm).some((value) => value)) {
      llm["gpt-4o-mini"] = true;
    }

    setLLM({ ...llm });
    localStorage.setItem(`LLM_${nodeId}`, JSON.stringify(llm));
    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          llm_params: {
            ...prevData.workflow[`${nodeId}`].llm_params,
            llm: llm,
          },
        },
      },
    }));
  };

  const handleRankChange = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());
    setRank(value);

    // Create an object for temperature values
    const newRanks = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});

    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          finetuning_values: {
            ...prevData.workflow[`${nodeId}`].finetuning_values,
            rank: newRanks,
          },
        },
      },
    }));
    localStorage.setItem(`rank_${nodeId}`, JSON.stringify(value));
  };

  const handleAlphaRankRatio = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());
    setAlphaRankRatio(value);

    // Create an object for temperature values
    const newAlphaRank = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});

    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          finetuning_values: {
            ...prevData.workflow[`${nodeId}`].finetuning_values,
            alphaRank: newAlphaRank,
          },
        },
      },
    }));

    localStorage.setItem(`aplhaRankRatio_${nodeId}`, JSON.stringify(value));
  };

  const handleTemperatureChange = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());

    setTemperature(value);
    // Create an object for temperature values
    const newTemperatureValues = values.reduce((obj, val) => {
      if (parseFloat(val) > 1) {
        return obj;
      } else {
        obj[val.trim()] = true;
        return obj;
      }
    }, {});

    setBotData((prevData) => {
      const updatedWorkflow = { ...prevData.workflow }; // Copy the entire workflow

      // Update only the specific node's state
      updatedWorkflow[nodeId] = {
        ...updatedWorkflow[nodeId], // Preserve other properties of this node
        llm_params: {
          ...updatedWorkflow[nodeId].llm_params,
          temperature: newTemperatureValues,
        },
      };

      return { ...prevData, workflow: updatedWorkflow };
    });
    localStorage.setItem(`temperature_${nodeId}`, JSON.stringify(value));
  };

  const handleOutputTokensLimitChange = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());

    setoutputTokenLimit(value);

    // Create an object for temperature values
    const newTokenLimits = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});

    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          llm_params: {
            ...prevData.workflow[`${nodeId}`].llm_params,
            outputTokenLimit: newTokenLimits,
          },
        },
      },
    }));
  };

  const handleBatchSize = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());

    setbatchSize(value);

    // Create an object for temperature values
    const newBatches = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});
    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          finetuning_values: {
            ...prevData.workflow[`${nodeId}`].finetuning_values,
            batchSize: newBatches,
          },
        },
      },
    }));
    localStorage.setItem(`batchSize_${nodeId}`, JSON.stringify(value));
  };

  const handleLearningRateChange = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());

    setLearningRate(value);

    // Create an object for temperature values
    const newLearningRates = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});

    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          finetuning_values: {
            ...prevData.workflow[`${nodeId}`].finetuning_values,
            learningRate: newLearningRates,
          },
        },
      },
    }));
    localStorage.setItem(`learningRateM_${nodeId}`, JSON.stringify(value));
  };

  const handleLearningRateMultiplierChange = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());
    setLearningRateM(value);

    // Create an object for temperature values
    const newLearningRates = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});

    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          finetuning_values: {
            ...prevData.workflow[`${nodeId}`].finetuning_values,
            learningRate: newLearningRates,
          },
        },
      },
    }));
    localStorage.setItem(`learningRateM_${nodeId}`, JSON.stringify(value));
  };

  const handleEpochChange = (event) => {
    const value = event.target.value;
    const values = value.split(",").map((val) => val.trim());

    setEpoch(value);

    // Create an object for temperature values
    const newEpochs = values.reduce((obj, val) => {
      obj[val.trim()] = true;
      return obj;
    }, {});

    setBotData((prevData) => ({
      ...prevData,
      workflow: {
        ...prevData.workflow,
        [`${nodeId}`]: {
          ...prevData.workflow[`${nodeId}`],
          finetuning_values: {
            ...prevData.workflow[`${nodeId}`].finetuning_values,
            epoch: newEpochs,
          },
        },
      },
    }));
    localStorage.setItem(`epoch_${nodeId}`, JSON.stringify(value));
  };

  const handleLLMFinetuningChange = (e) => {
    const isSelected = e.target.checked;

    setLLMFinetuning(isSelected);
    localStorage.setItem(`LLMFinetuning_${nodeId}`, JSON.stringify(isSelected));
    setBotData((prevData) => ({
      ...prevData,
      "llm-finetuning": isSelected,
    }));
  };

  const handleLLMFoundationalChange = (e) => {
    setLLMFoundational(e.target.checked);
    localStorage.setItem(`LLMFoundational_${nodeId}`, e.target.checked);
    setBotData((prevData) => ({ ...prevData, foundational: e.target.checked }));
  };

  const handleChangeAdvancedOption = (e) => {
    setAdvancedOption(e.target.checked);
    localStorage.setItem(`advancedOption_${nodeId}`, e.target.checked);
  };

  const handleGeneratePrompt = async () => {
    const _localdata_ = JSON.parse(localStorage.getItem("problem_statement"));
    const user_id = user?.user?._id["$oid"];
    await setBotData((prevData) => ({ ...prevData, rqa_prompt: "" }));
    const unFilteredPrompts = { "zero-shot": true };
    const prompt_types = Object.keys(unFilteredPrompts).filter(
      (_) => unFilteredPrompts[_] === true
    );
    let prompt_combinations = {};
    let saved_prompts = {};
    let retrieval_check = false;
    // Assuming botData.workflow is your object
    const workflow = botData.workflow;
    const keys = Object.keys(workflow);
    const retrievalKeys = keys.filter((key) => key.startsWith("retrieval_"));
    const generationKeys = keys.filter((key) => key.startsWith("generation_"));

    if (retrievalKeys.length > 0 && generationKeys.length > 0) {
      const firstRetrievalKey = retrievalKeys[0];
      const firstGenerationKey = generationKeys[0];

      const retrievalIndex = keys.indexOf(firstRetrievalKey);
      const generationIndex = keys.indexOf(firstGenerationKey);

      if (retrievalIndex < generationIndex) {
        retrieval_check = true;
      } else {
        retrieval_check = false;
      }
    } else {
      if (retrievalKeys.length > 0) {
        retrieval_check = true;
      } else {
        retrieval_check = false;
      }
    }

    const formData = new FormData();
    for (const prompt_type of prompt_types) {
      let _data = {
        problem_statement: _localdata_,
        prompt_type,
        prompt_text:
          botData.response_length_text +
          botData.recency_text +
          botData.tone_text +
          botData.sources_text,
      };

      if (prompt_type === "dynamic-shot-random") {
        _data["prompt_type"] = "dynamic-shot-random";
        _data["dataset"] = dataset;
      } else {
        if (_data["dataset"]) {
          let new_data = { ..._data };
          delete new_data["dataset"];
          _data = { ...new_data };
        }
      }

      if (retrieval_check) {
        _data["retrieval"] = "true";
      } else {
        if (_data["retrieval"]) {
          delete _data["retreival"];
        }
      }

      for (const [key, value] of Object.entries(_data)) {
        let d_ = {};
        if (key === "dataset") {
          botData.dynamicShot_dataset.map((d, index) => (d_[key + index] = d));
          formData.append(key, JSON.stringify(d_));
          continue;
        } else if (key === "prompt_text") {
          formData.append(key, JSON.stringify(value));
          continue;
        } else if (key === "qa_dict") {
          formData.append(key, JSON.stringify(value));
          continue;
        }

        formData.append(key, value);
      }

      const apiEndpoint = `/api/eval_bot/generate/prompt/${prompt_type}/${user_id}`;
      const token = JSON.parse(localStorage.getItem("token"));
      // Making the POST request
      await axios
        .post(apiEndpoint, formData, {
          headers: {
            // Include the bearer token in the authorization header
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          // Handle success
          const prompt = response.data.prompt;
          prompt_combinations[prompt_type] = prompt;
          saved_prompts[prompt_type] = prompt;
        })
        .catch((error) => {
          // Handle error
          console.error("Error:", error);
        });
    }
    let prompt_str = "";
    const run = Object.keys(saved_prompts).map(
      (_) => (prompt_str += `${_}\n\n${saved_prompts[_]}\n\n\n`)
    );
    setBasicPrompt(prompt_combinations);
    localStorage.setItem(
      `basicPrompt_${nodeId}`,
      JSON.stringify(prompt_combinations)
    );

    // remove rqa prompt
    setBotData((prevData) => ({
      ...prevData,
      rqa_prompt: prompt_str,
      prompt_combinations: { ...prompt_combinations },
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "auto",
        background: theme.main.applicationBackground,
        overflowY: "auto",
        width: hidden ? "auto" : "800px",
        borderRadius: "10px",
        ...scrollbarStyles,
      }}
      data-tour="option-2a"
    >
      <Tooltip
        title="Press the backspace key to delete this node."
        placement="top"
      >
        <Box sx={hidden ? topNavNodeHidden : topNavNode}>
          <Typography sx={topNavNodeText}>LLM</Typography>
          <div className="icon-container">
            <Tooltip title="Click for more Information">
              <InfoOutlined
                onClick={(event) => handleOpenSideBar("generation", event)}
              />
            </Tooltip>{" "}
            <div
              className="icon-container"
              data-tour="option-2b"
              onClick={() => {
                setHidden((prevState) => {
                  const newState = !prevState;
                  localStorage.setItem(
                    `hidden_${nodeId}`,
                    JSON.stringify(newState)
                  );
                  return newState;
                });
              }}
            >
              <Tooltip
                title={
                  hidden ? "Show Advanced Options" : "Hide Advanced Options"
                }
                placement="top"
              >
                {hidden ? (
                  <Fullscreen color="white" sx={{ transform: "scaleY(-1)" }} />
                ) : (
                  <FullscreenExit color="white" />
                )}
              </Tooltip>
            </div>
            <Tooltip title="Need Help?">
              <HelpOutline onClick={() => handleGenerationTutorial()} />
            </Tooltip>
            <Tooltip title="Delete this tool">
              <IconButton
                sx={{ ...deleteIconStyles, ml: 1 }}
                onClick={() => data.handleDeleteSelected(nodeId)}
              >
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </Box>
      </Tooltip>

      {hidden ? (
        <Box
          sx={{
            backgroundColor: theme.main.applicationBackground,
            padding: 2,
            width: "400px",
            color: theme.textColors.dark.primary,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              border: `1px solid ${theme.border.light}`,
              padding: 2,
              borderRadius: "10px",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              Prompt:
            </Typography>
            {Object.entries(basicPrompt).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  borderTop: `2px solid ${theme.border.light}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  {key}
                </Typography>
                <Typography>
                  {value.length > 30 ? value.slice(0, 30) + "..." : value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              border: `1px solid ${theme.border.light}`,
              padding: 2,
              borderRadius: "10px",
              mt: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
              }}
            >
              (Selected LLM:{" "}
              {Object.keys(LLM)
                .filter((_) => LLM?.[_] === true)
                .join(", ")}
              )
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            padding: 2,
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", flexGrow: 1, textAlign: "center" }}>
            <Tabs
              centered
              value={selectedTab}
              onChange={handleChangeTab}
              sx={{
                ...tabsStyle,
                flexWrap: "nowrap", // Prevent wrapping of tabs
                ".MuiTab-root": {
                  whiteSpace: "nowrap", // Ensures the text doesn't wrap
                },
              }}
            >
              <Tab
                label={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox
                      customStyles={{ width: "20px", height: "20px" }}
                      label=""
                      onChange={(event) => handleLLMFoundationalChange(event)}
                      checked={
                        LLMFoundational === "false" || LLMFoundational === false
                          ? false
                          : LLMFoundational
                      }
                    />{" "}
                    Foundational Model
                  </Box>
                }
              />
              <Tab
                label={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox
                      customStyles={{ width: "20px", height: "20px" }}
                      label=""
                      onChange={(event) => handleLLMFinetuningChange(event)}
                      checked={LLMFinetuning}
                      disabled={
                        !(
                          organization?.organization?.subscription ===
                          "enteprise"
                        )
                      }
                      tooltipText={
                        !(
                          organization?.organization?.subscription ===
                          "enterprise"
                        ) &&
                        "You need Enterprise subscription to access this feature."
                      }
                      // disabled={true}
                      // tooltipText={
                      //   organization?.organization?.subscription ===
                      //     "starter" &&
                      //   "You need Pro subscription to access this feature."
                      // }
                    />{" "}
                    Fine-tuning
                  </Box>
                }
              />
            </Tabs>
          </Box>
          <Box sx={{ padding: 2, width: "100%", flexGrow: 1 }}>
            {selectedTab === 1 && (
              <FinetuningNode
                LLMFinetuning={LLMFinetuning}
                handleLLMSelectClick={handleLLMSelectClick}
                selectRefLLM={selectRefLLM}
                llm_select={llm_select}
                handleLLMSelectChange={handleLLMSelectChange}
                llm_select_open={llm_select_open}
                setllm_select_open={setllm_select_open}
                llm_select_options={llm_select_options}
                handleOpmSelectClick={handleOpmSelectClick}
                selectRefOPM={selectRefOPM}
                opm_select={opm_select}
                handleOpmSelectChange={handleOpmSelectChange}
                opm_select_open={opm_select_open}
                setopm_select_open={setopm_select_open}
                opm_select_options={opm_select_options}
                botData={botData}
                setBotData={setBotData}
                nodeId={nodeId}
                batchSize={batchSize}
                handleBatchSize={handleBatchSize}
                learningRate={learningRate}
                handleLearningRateChange={handleLearningRateChange}
                learningRateM={learningRateM}
                handleLearningRateMultiplierChange={
                  handleLearningRateMultiplierChange
                }
                rank={rank}
                handleRankChange={handleRankChange}
                aplhaRankRatio={aplhaRankRatio}
                handleAlphaRankRatio={handleAlphaRankRatio}
                organization={organization}
                epoch={epoch}
                data={data}
                handleEpochChange={handleEpochChange}
              />
            )}
            {selectedTab === 0 && (
              <FoundationalModels
                LLMFoundational={LLMFoundational}
                handleLLMFoundationalChange={handleLLMFoundationalChange}
                expandedPanel={expandedPanel}
                setExpandedPanel={setExpandedPanel}
                LLM={LLM}
                handleLLMChange={handleLLMChange}
                temperature={temperature}
                handleTemperatureChange={handleTemperatureChange}
                outputTokenLimit={outputTokenLimit}
                handleOutputTokensLimitChange={handleOutputTokensLimitChange}
                data={data}
                basicPrompt={basicPrompt}
                setBasicPrompt={setBasicPrompt}
                organization={organization?.organization}
                handleOpenSideBar={handleOpenSideBar}
              />
            )}
          </Box>
          <LLMNodeSelectedValues
            handleGeneratePrompt={handleGeneratePrompt}
            LLM={LLM}
            basicPrompt={basicPrompt}
            LLMFinetuning={LLMFinetuning}
          />
        </Box>
      )}

      <Handle
        type="target"
        position={Position.Left}
        style={{
          top: hidden ? "70%" : "2%",
          backgroundColor: theme.main.primaryBlue,
          height: "14px",
          width: "14px",
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          top: hidden ? "70%" : "2%",
          backgroundColor: theme.main.primaryBlue,
          height: "14px",
          width: "14px",
        }}
      />
    </Box>
  );
});

export default LLMNode;
